import React, { useEffect, useMemo, useState } from 'react';
import {
  stockPortfolioService,
  StockAccountOption,
  StockGrowthChallenge,
  StockGrowthChallengeLadderStep,
} from '../../services/stockPortfolioService';

const MIN_START = 10;
const MAX_START = 50;

function fmtUsd(n: number): string {
  return `$${n.toFixed(2).replace(/\.00$/, '')}`;
}

function defaultLadder(start: number): StockGrowthChallengeLadderStep[] {
  return [
    { atUsd: start * 2, lockUsd: start * 0.5 },
    { atUsd: start * 4, lockUsd: start },
    { atUsd: start * 7, lockUsd: start * 2 },
  ];
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#334155',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 13,
  boxSizing: 'border-box',
};

interface StockGrowthChallengeWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (challenge: StockGrowthChallenge) => void;
  defaultAccountId?: number;
}

const StockGrowthChallengeWizard: React.FC<StockGrowthChallengeWizardProps> = ({
  open,
  onClose,
  onCreated,
  defaultAccountId,
}) => {
  const [accounts, setAccounts] = useState<StockAccountOption[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [t212ConfigId, setT212ConfigId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [startAmount, setStartAmount] = useState('10');
  const [targetAmount, setTargetAmount] = useState('100');
  const [floorAmount, setFloorAmount] = useState('5');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmation('');
    setLoadingMeta(true);
    stockPortfolioService.listStockAccounts()
      .then(accs => {
        setAccounts(accs);
        if (defaultAccountId && accs.some(a => a.id === defaultAccountId)) {
          setT212ConfigId(defaultAccountId);
        } else if (accs[0]) {
          setT212ConfigId(accs[0].id);
        }
      })
      .catch(() => setError('Failed to load Trading 212 accounts'))
      .finally(() => setLoadingMeta(false));
  }, [open, defaultAccountId]);

  const start = Number(startAmount);
  const target = Number(targetAmount);
  const floor = Number(floorAmount);
  const startValid = Number.isFinite(start) && start >= MIN_START && start <= MAX_START;
  const expectedConfirmation = startValid ? `RISK $${start % 1 === 0 ? start.toFixed(0) : start}` : 'RISK $…';
  const ladder = useMemo(() => (startValid ? defaultLadder(start) : []), [startValid, start]);

  const canSubmit = !saving
    && !loadingMeta
    && t212ConfigId !== ''
    && startValid
    && Number.isFinite(target) && target > start
    && Number.isFinite(floor) && floor >= 0 && floor < start
    && confirmation.trim().toUpperCase() === expectedConfirmation.toUpperCase();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const challenge = await stockPortfolioService.createStockGrowthChallenge({
        name: name.trim() || null,
        t212ConfigId: Number(t212ConfigId),
        startBalance: start,
        targetBalance: target,
        floorBalance: floor,
        ladder,
        confirmationText: confirmation.trim(),
      });
      onCreated(challenge);
      onClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create challenge';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 20,
          boxShadow: '0 20px 45px rgba(15, 23, 42, 0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Start a Stock Growth Challenge</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              One long-only bot on Trading 212 — MARKET buy + STOP loss, polled take-profit exits.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            margin: '12px 0',
            padding: '10px 12px',
            borderRadius: 10,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <strong>High risk:</strong> use a dedicated DEMO account first. Daily order cap is 20 per T212 account;
          each entry uses ~2 orders (buy + stop). Max order value $500. Only commit money you can afford to lose.
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 12 }}>
            {error}
          </div>
        )}

        <label style={labelStyle}>
          Trading 212 account
          <select
            value={t212ConfigId}
            onChange={e => setT212ConfigId(e.target.value ? Number(e.target.value) : '')}
            style={inputStyle}
            disabled={loadingMeta}
          >
            {accounts.length === 0 && <option value="">No accounts</option>}
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.environment})
              </option>
            ))}
          </select>
        </label>

        <label style={{ ...labelStyle, marginTop: 12 }}>
          Challenge name (optional)
          <input type="text" value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="Stock Challenge $10 → $100" />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 12 }}>
          <label style={labelStyle}>
            Start ($10–50)
            <input type="number" min={MIN_START} max={MAX_START} value={startAmount} onChange={e => setStartAmount(e.target.value)} style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Target
            <input type="number" value={targetAmount} onChange={e => setTargetAmount(e.target.value)} style={inputStyle} />
          </label>
          <label style={labelStyle}>
            Floor
            <input type="number" value={floorAmount} onChange={e => setFloorAmount(e.target.value)} style={inputStyle} />
          </label>
        </div>

        {startValid && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
            Default ladder: {ladder.map((s, i) => (
              <span key={i}>{i > 0 ? ' · ' : ''}{fmtUsd(s.atUsd)} → lock {fmtUsd(s.lockUsd)}</span>
            ))}
          </div>
        )}

        <label style={{ ...labelStyle, marginTop: 16 }}>
          Type <strong>{expectedConfirmation}</strong> to confirm
          <input type="text" value={confirmation} onChange={e => setConfirmation(e.target.value)} style={inputStyle} autoComplete="off" />
        </label>

        <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            style={{
              padding: '8px 18px',
              borderRadius: 999,
              border: 'none',
              background: canSubmit ? '#086afb' : '#94a3b8',
              color: '#fff',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {saving ? 'Creating…' : 'Start challenge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockGrowthChallengeWizard;
